import { MarkHeading } from '../../../components/MarkControls';
import * as layout from '../../../components/markLayout.module.css';
import {
    MarkChoiceLabel,
    MarkChoice,
    MarkInput,
    MarkLabel
} from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import IrreversibleWarning from './IrreversibleWarning';
import type { DripTriggerType, ProspectType } from '../../../types';
export type Step1Data = {
    name: string;
    triggerType: DripTriggerType;
    prospectType: ProspectType;
};
type Props = {
    data: Step1Data;
    onChange: (data: Step1Data) => void;
    errors: string[];
};
const Step1Identity: React.FC<Props> = ({ data, onChange, errors }) => {
    const nameId = useId();
    const [nameTouched, setNameTouched] = useState(false);
    const nameInvalid = !data.name.trim() && (nameTouched || errors.includes('Name is required.'));
    const otherErrors = errors.filter((error) => error !== 'Name is required.');
    const triggerGroupId = useId();
    const prospectGroupId = useId();
    return (
        <div className={layout.form}>
            <IrreversibleWarning />
            <div className={layout.field}>
                <MarkLabel htmlFor={nameId}>
                    Drip name <span aria-hidden="true">*</span>
                </MarkLabel>
                <MarkInput
                    id={nameId}
                    data-field="name"
                    type="text"
                    value={data.name}
                    required
                    aria-required="true"
                    aria-invalid={nameInvalid}
                    aria-describedby={nameInvalid ? `${nameId}-error` : undefined}
                    onBlur={() => setNameTouched(true)}
                    onChange={(e) => {
                        setNameTouched(true);
                        onChange({ ...data, name: e.currentTarget.value });
                    }}
                    placeholder="e.g. Welcome sequence"
                />
                {nameInvalid && (
                    <p id={`${nameId}-error`} role="alert" className={layout.fieldError}>
                        Name is required.
                    </p>
                )}
            </div>
            <fieldset
                aria-labelledby={triggerGroupId}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                <MarkHeading id={triggerGroupId} role="h6">
                    Trigger type
                </MarkHeading>
                <div
                    role="radiogroup"
                    aria-labelledby={triggerGroupId}
                    className={layout.choiceGroup}
                >
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="trigger-type"
                            data-field="trigger-type"
                            value="PROSPECT"
                            checked={data.triggerType === 'PROSPECT'}
                            onChange={() => onChange({ ...data, triggerType: 'PROSPECT' })}
                        />
                        Prospect
                    </MarkChoiceLabel>
                    <MarkChoiceLabel>
                        <MarkChoice
                            type="radio"
                            name="trigger-type"
                            value="SEGMENT"
                            checked={data.triggerType === 'SEGMENT'}
                            onChange={() => onChange({ ...data, triggerType: 'SEGMENT' })}
                        />
                        Segment
                    </MarkChoiceLabel>
                </div>
            </fieldset>
            {data.triggerType === 'PROSPECT' && (
                <fieldset
                    aria-labelledby={prospectGroupId}
                    style={{
                        margin: 0
                    }}
                    className={layout.fieldset}
                >
                    <MarkHeading id={prospectGroupId} role="h6">
                        Prospect type
                    </MarkHeading>
                    <div
                        role="radiogroup"
                        aria-labelledby={prospectGroupId}
                        className={layout.choiceGroup}
                    >
                        <MarkChoiceLabel>
                            <MarkChoice
                                type="radio"
                                name="prospect-type"
                                data-field="prospect-type"
                                value="D2C"
                                checked={data.prospectType === 'D2C'}
                                onChange={() => onChange({ ...data, prospectType: 'D2C' })}
                            />
                            D2C
                        </MarkChoiceLabel>
                        <MarkChoiceLabel>
                            <MarkChoice
                                type="radio"
                                name="prospect-type"
                                value="RTM"
                                checked={data.prospectType === 'RTM'}
                                onChange={() => onChange({ ...data, prospectType: 'RTM' })}
                            />
                            RTM
                        </MarkChoiceLabel>
                    </div>
                </fieldset>
            )}
            {otherErrors.length > 0 && (
                <div role="alert" aria-live="polite" className={layout.notice}>
                    {otherErrors.map((e) => (
                        <P
                            key={e}
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {e}
                        </P>
                    ))}
                </div>
            )}
        </div>
    );
};
export default Step1Identity;
